/**
 * Universal Search API client (S2, issue #492 — phase 2 of epic #490).
 *
 * Typed wrapper over `GET /api/search/`, layered on the shared `apiRequest`
 * (session cookie + `X-Tenant` header, see `client.ts`). The caller is
 * responsible for the client-side `< MIN_QUERY_LENGTH` short-circuit (issue
 * #491's contract: the SERVER runs zero DB queries below this length, and the
 * palette must mirror that by making ZERO fetch calls too — not just
 * debouncing them away).
 */
import { apiRequest } from "./client";
import type { SearchResponse } from "./search.types";

/** Mirrors `apps.search.engine.MIN_QUERY_LENGTH` — below this, no fetch at all. */
export const MIN_QUERY_LENGTH = 2;

/** The palette's debounce window between the last keystroke and the fetch. */
export const SEARCH_DEBOUNCE_MS = 200;

/** Per-group result cap sent as `?limit=`. Mirrors the backend's `DEFAULT_LIMIT`. */
export const DEFAULT_SEARCH_LIMIT = 8;

/**
 * `GET /api/search/?q=<term>&limit=<n>`. Pass `signal` to make the request
 * abortable (the palette aborts a superseded in-flight request before issuing
 * a newer one — see `components/search-palette/SearchPalette.tsx`).
 */
export function search(
  query: string,
  opts: { limit?: number; signal?: AbortSignal } = {},
): Promise<SearchResponse> {
  const params = new URLSearchParams({ q: query });
  params.set("limit", String(opts.limit ?? DEFAULT_SEARCH_LIMIT));
  return apiRequest<SearchResponse>(`/search/?${params.toString()}`, {
    signal: opts.signal,
  });
}
